'use client';

import {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import type {
  AdminTaxReturnsProviderProps,
  PaginationState,
} from './AdminTaxReturnsProvider.types';
import { TaxReturn, VoucherDefinition } from '@/_types/models/taxReturns';
import { UserContext } from '@/_contexts/UserProvider';
import { TaxReturnSendDialog } from './components/TaxReturnSendDialog';
import {
  getPaginatedTaxReturns,
  getFormDefinitions,
  getVoucherDefinitions,
  downloadTaxReturn,
  getTaxReturnById,
} from '@/_api/admin-tax-returns';
import { UploadTaxReturnDialog } from './components/UploadTaxReturnDialog';
import { TaxReturnVoucherDialog } from './components/TaxReturnVoucherDialog';
import { TaxReturnSignatureDialog } from './components/TaxReturnSignatureDialog';
import { areSameObjects } from '@/_utils/objects';
import { TaxReturnActivityLogDialog } from './components/TaxReturnActivityLogDialog';
import { TaxReturnIdentityVerificationDialog } from './components/TaxReturnIdentityVerificationDialog';
import {
  DEFAULT_DELIVER_FILTER,
  DELIVER_STATUS_TEXT_MAP,
  DELIVER_STATUS_TYPES,
  DELIVER_TABLE_DEFAULT_SORTING,
} from '@/_constants/tax-return';
import { ToastContext } from '@/_contexts/ToastProvider';
import { getCookie, setCookie } from 'cookies-next/client';
import {
  TAX_RETURNS_PAGE_SIZE_COOKIE,
  MAX_COOKIE_AGE,
} from '@/_constants/cookies';
import { _downloadBlob } from '@/_api/api';
import type {
  DeliverFilterObject,
  FormDefinition,
} from '@/_types/taxReturnDelivery';
import type { SelectItem } from '@/_components';
import { TaxReturnReminderDialog } from './components/TaxReturnReminderDialog';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

const AdminTaxReturnsContext = createContext<AdminTaxReturnsProviderProps>({
  availableStatusItems: [],
  downloadTaxReturn: () => Promise.resolve(),
  filter: DEFAULT_DELIVER_FILTER,
  formDefinitions: [],
  getTaxReturns: () => Promise.resolve(),
  hasInitiallyLoaded: false,
  isAdvisorLoading: false,
  isFiltering: false,
  isSending: false,
  isSorting: false,
  onFilterChange: () => null,
  pagination: { pageIndex: 0, pageSize: 10, totalPages: 0, totalCount: 0 },
  reloadSelectedTaxReturn: () => Promise.resolve(),
  resetFilters: () => null,
  selectedPaymentVouchersReturn: undefined,
  selectedSignaturesReturn: undefined,
  selectedReminderReturn: undefined,
  selectedTaxReturn: undefined,
  selectedTaxReturnLoading: false,
  selectedTaxReturnExport: undefined,
  selectedTaxReturnForActivity: undefined,
  selectedTaxReturnForReplacement: undefined,
  selectedTaxReturnImages: [],
  selectedTaxReturns: [],
  setPagination: () => {},
  setSelectedIdentityVerificationReturn: () => null,
  setSelectedPaymentVouchersReturn: () => {},
  setSelectedSignaturesReturn: () => {},
  setSelectedTaxReturn: () => {},
  setSelectedTaxReturnExport: () => {},
  setSelectedTaxReturnForActivity: () => null,
  setSelectedTaxReturnForReplacement: () => null,
  setSelectedTaxReturnImages: () => {},
  setSelectedTaxReturns: () => {},
  setSelectedReminderReturn: () => {},
  setSending: () => null,
  setSorting: () => null,
  sorting: DELIVER_TABLE_DEFAULT_SORTING,
  taxReturns: [],
  voucherDefinitions: [],
  currentTaxReturnIdForVue: null,
  setCurrentTaxReturnIdForVue: () => null,
  pollingEnabled: false,
});

const AdminTaxReturnsProvider = (props: { children: ReactNode }) => {
  const { isAdvisor, user, token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [filter, setFilter] = useState<DeliverFilterObject>(
    DEFAULT_DELIVER_FILTER,
  );
  const [selectedTaxReturn, setSelectedTaxReturn] = useState<
    TaxReturn | undefined
  >(undefined);
  const [taxReturns, setTaxReturns] = useState<Array<TaxReturn>>([]);
  const [selectedTaxReturns, setSelectedTaxReturns] = useState<
    Array<TaxReturn>
  >([]);
  const [currentTaxReturnIdForVue, setCurrentTaxReturnIdForVue] = useState<
    number | null
  >(null);
  const [isSending, setSending] = useState<boolean>(false);
  const [isAdvisorLoading, setAdvisorLoading] = useState<boolean>(true);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState<boolean>(false);
  const [voucherDefinitions, setVoucherDefinitions] = useState<
    Array<VoucherDefinition>
  >([]);
  const [formDefinitions, setFormDefinitions] = useState<Array<FormDefinition>>(
    [],
  );
  const [selectedTaxReturnForActivity, setSelectedTaxReturnForActivity] =
    useState<TaxReturn | undefined>(undefined);
  const [selectedPaymentVouchersReturn, setSelectedPaymentVouchersReturn] =
    useState<TaxReturn | undefined>(undefined);
  const [
    selectedIdentityVerificationReturn,
    setSelectedIdentityVerificationReturn,
  ] = useState<TaxReturn | undefined>(undefined);
  const [selectedSignaturesReturn, setSelectedSignaturesReturn] = useState<
    TaxReturn | undefined
  >(undefined);
  const [selectedReminderReturn, setSelectedReminderReturn] = useState<
    TaxReturn | undefined
  >(undefined);

  const [pagination, setPagination] = useState<PaginationState>(() => {
    const pageSizeCookie = getCookie(TAX_RETURNS_PAGE_SIZE_COOKIE);
    const initialPageSize: number = pageSizeCookie
      ? parseInt(pageSizeCookie as string)
      : 10;

    return {
      totalPages: 0,
      totalCount: 0,
      pageIndex: 0,
      pageSize: !isNaN(initialPageSize) ? initialPageSize : 10,
    };
  });
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>(
    [],
  );
  const [selectedTaxReturnForReplacement, setSelectedTaxReturnForReplacement] =
    useState<TaxReturn | undefined>(undefined);
  const [selectedTaxReturnLoading, setSelectedTaxReturnLoading] =
    useState<boolean>(false);
  const [selectedTaxReturnExport, setSelectedTaxReturnExport] = useState<
    TaxReturn | undefined
  >(undefined);
  const [selectedTaxReturnImages, setSelectedTaxReturnImages] = useState<
    Array<{ page: number; url: string }>
  >([]);

  const pollingEnabled = useMemo(() => {
    return (
      !isAdvisorLoading &&
      !selectedPaymentVouchersReturn?.id &&
      !selectedSignaturesReturn?.id &&
      !selectedIdentityVerificationReturn?.id &&
      !selectedTaxReturnForActivity?.id &&
      !selectedTaxReturnExport?.id &&
      !selectedReminderReturn?.id
    );
  }, [
    isAdvisorLoading,
    selectedPaymentVouchersReturn,
    selectedSignaturesReturn,
    selectedIdentityVerificationReturn,
    selectedTaxReturnForActivity,
    selectedTaxReturnExport,
    selectedReminderReturn,
  ]);

  const onFilterChange = (newFilter: Partial<DeliverFilterObject | object>) => {
    const newFilters: DeliverFilterObject = {
      ...filter,
      ...newFilter,
    };

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

    return setFilter(newFilters);
  };

  const resetFilters = () => {
    setFilter(DEFAULT_DELIVER_FILTER);
  };

  const AVAILABLE_STATUS_ITEMS: Array<SelectItem> = useMemo(() => {
    if (isAdvisorLoading) return [];

    const list: Array<SelectItem> = DELIVER_STATUS_TYPES.map((s) => ({
      name: DELIVER_STATUS_TEXT_MAP[s as keyof typeof DELIVER_STATUS_TEXT_MAP],
      value: s,
    }));

    return [{ name: 'All', value: '' }, ...list];
  }, [isAdvisorLoading]);

  const isFiltering: boolean = useMemo(() => {
    return !areSameObjects(filter, DEFAULT_DELIVER_FILTER);
  }, [filter]);

  const isSorting: boolean = useMemo(() => {
    if (sorting?.length !== 1) return false;

    return !areSameObjects(sorting?.[0], DELIVER_TABLE_DEFAULT_SORTING);
  }, [sorting]);

  const _getPaginatedTaxReturns = useCallback(
    async (isSilent?: boolean) => {
      if (
        !isAdvisor ||
        !token ||
        !preferences?.new_tax_return_delivery_enabled
      ) {
        return;
      }

      try {
        if (!isSilent) setAdvisorLoading(true);

        const { tax_returns, meta } = await getPaginatedTaxReturns({
          token,
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          filter,
          ...(sorting?.[0] && {
            sort_by: sorting[0].id,
            sort_direction: sorting[0].desc ? 'desc' : 'asc',
          }),
        });

        if (Array.isArray(tax_returns)) {
          setTaxReturns(tax_returns);
          setPagination((prev) => ({
            ...prev,
            pageIndex: (meta?.current_page ?? 0) - 1,
            totalCount: meta?.total_count ?? 0,
            totalPages: meta?.total_pages ?? 0,
          }));
        }

        setHasInitiallyLoaded(true);
      } catch (error) {
        console.error(error);
        setTaxReturns([]);
        setPagination((prev) => ({ ...prev, totalPages: 0, totalCount: 0 }));
      } finally {
        setAdvisorLoading(false);
      }
    },
    [
      token,
      isAdvisor,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      filter,
      preferences?.new_tax_return_delivery_enabled,
    ],
  );

  const _downloadTaxReturn = useCallback(
    async (
      taxReturn: TaxReturn,
      includeOtherPages: boolean = false,
      onlySignaturePages: boolean = false,
    ) => {
      try {
        const response = await downloadTaxReturn({
          id: taxReturn.id,
          companyId: taxReturn.company_id,
          includeOtherPages,
          onlySignaturePages,
          token,
        });

        if (!response?.link) {
          addToast('error', 'Error downloading tax return');
          throw new Error('Download link not found in response');
        }

        if (!preferences?.tmp_trd_download_notification_enabled) {
          await _downloadBlob({
            url: response?.link,
            fileName: `${taxReturn.tax_return_name}${onlySignaturePages ? ' Signature Pages' : ''}.pdf`,
          });
        } else {
          addToast(
            'success',
            'You will receive a notification when the tax return is ready to be downloaded.',
          );
        }
      } catch (error) {
        console.error('Error downloading tax return:', error);
        addToast('error', 'Error downloading tax return');
      }
    },
    [token, addToast, preferences?.tmp_trd_download_notification_enabled],
  );

  const onCloseActivityLog = () => setSelectedTaxReturnForActivity(undefined);
  const onCloseSignatureDialog = () => setSelectedSignaturesReturn(undefined);
  const onCloseReminderDialog = () => setSelectedReminderReturn(undefined);

  const _getDefinitions = useCallback(async () => {
    if (
      !token ||
      !user ||
      !isAdvisor ||
      !preferences?.new_tax_return_delivery_enabled
    ) {
      return;
    }

    try {
      const definitions = await getVoucherDefinitions({ token });
      setVoucherDefinitions(definitions);

      const formDefinitions = await getFormDefinitions({ token });
      setFormDefinitions(formDefinitions);
    } catch (error) {
      console.error(error);
    }
  }, [token, user, isAdvisor, preferences?.new_tax_return_delivery_enabled]);

  const handleUpdatePagination = useCallback(
    (newPagination: PaginationState) => {
      setPagination((prev) => ({
        ...prev,
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      }));
    },
    [],
  );

  const reloadSelectedTaxReturn = async () => {
    if (!token) return;
    if (typeof selectedTaxReturn?.id !== 'number') return;

    try {
      setSelectedTaxReturnLoading(true);
      const taxReturn = await getTaxReturnById({
        token,
        id: selectedTaxReturn?.id!,
        company_id: selectedTaxReturn?.company_id!,
      });

      if (taxReturn?.error) throw new Error('failed to load tax return');

      setSelectedTaxReturn(taxReturn);
    } catch (error) {
      console.error(error);
    } finally {
      setSelectedTaxReturnLoading(false);
    }
  };

  useEffect(() => {
    _getDefinitions();
  }, [_getDefinitions]);

  useEffect(() => {
    _getPaginatedTaxReturns();
  }, [_getPaginatedTaxReturns]);

  useEffect(() => {
    if (pagination?.pageSize) {
      setCookie(TAX_RETURNS_PAGE_SIZE_COOKIE, pagination.pageSize.toString(), {
        maxAge: MAX_COOKIE_AGE,
        path: '/',
      });
    }
  }, [pagination?.pageSize]);

  return (
    <AdminTaxReturnsContext
      value={{
        availableStatusItems: AVAILABLE_STATUS_ITEMS,
        downloadTaxReturn: _downloadTaxReturn,
        filter,
        formDefinitions,
        getTaxReturns: _getPaginatedTaxReturns,
        hasInitiallyLoaded,
        isAdvisorLoading,
        isFiltering,
        isSending,
        isSorting,
        onFilterChange,
        pagination,
        reloadSelectedTaxReturn,
        resetFilters,
        selectedIdentityVerificationReturn,
        selectedPaymentVouchersReturn,
        selectedSignaturesReturn,
        selectedReminderReturn,
        selectedTaxReturn,
        selectedTaxReturnLoading,
        selectedTaxReturnExport,
        selectedTaxReturnForActivity,
        selectedTaxReturnForReplacement,
        selectedTaxReturnImages,
        selectedTaxReturns,
        setPagination: handleUpdatePagination,
        setSelectedIdentityVerificationReturn,
        setSelectedPaymentVouchersReturn,
        setSelectedSignaturesReturn,
        setSelectedReminderReturn,
        setSelectedTaxReturn,
        setSelectedTaxReturnExport,
        setSelectedTaxReturnForActivity,
        setSelectedTaxReturnForReplacement,
        setSelectedTaxReturnImages,
        setSelectedTaxReturns,
        setSending,
        setSorting,
        sorting,
        taxReturns,
        voucherDefinitions,
        currentTaxReturnIdForVue,
        setCurrentTaxReturnIdForVue,
        pollingEnabled,
      }}
    >
      <TaxReturnVoucherDialog isOpen={!!selectedPaymentVouchersReturn?.id} />
      <TaxReturnIdentityVerificationDialog
        isOpen={!!selectedIdentityVerificationReturn?.id}
      />
      <TaxReturnSignatureDialog
        isOpen={!!selectedSignaturesReturn?.id}
        onClose={onCloseSignatureDialog}
      />
      <TaxReturnSendDialog />
      <TaxReturnActivityLogDialog
        isOpen={!!selectedTaxReturnForActivity}
        onClose={onCloseActivityLog}
      />
      <TaxReturnReminderDialog
        isOpen={!!selectedReminderReturn?.id}
        onClose={onCloseReminderDialog}
        selectedTaxReturns={
          selectedReminderReturn ? [selectedReminderReturn] : []
        }
        title="Edit/Send Reminder Message"
      />
      <UploadTaxReturnDialog />
      {props.children}
    </AdminTaxReturnsContext>
  );
};

export { AdminTaxReturnsContext, AdminTaxReturnsProvider };
